import 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Box,
} from '@mui/material';
import {DatePicker} from '@/components/common/DatePicker';
import {MoneyField} from '@/components/common/MoneyField';

export interface MotorVehicleRow {
    date: string;
    item: string;
    atCost: number;
    accumulated: number;
    sales: number;
    disposal: number;
}

interface MotorVehicleTableProps {
    tableTitle: string;
    parentCurrency: string | null;
    storeRow: MotorVehicleRow[];
}

export function MotorVehicleTable({
                                      tableTitle,
                                      parentCurrency,
                                      storeRow,
                                  }: MotorVehicleTableProps) {

    if (!storeRow || storeRow.length === 0) {
        return null;
    }

    // Calculate totals
    const totals = storeRow.reduce(
        (acc, row) => {
            const nbv = row.atCost + row.accumulated;
            const gainOnDisposal = row.disposal >= 0 ? row.disposal : 0;
            const lossOnDisposal = row.disposal < 0 ? Math.abs(row.disposal) : 0;

            return {
                atCost: acc.atCost + row.atCost,
                accumulated: acc.accumulated + row.accumulated,
                nbv: acc.nbv + nbv,
                sales: acc.sales + row.sales,
                gainOnDisposal: acc.gainOnDisposal + gainOnDisposal,
                lossOnDisposal: acc.lossOnDisposal + lossOnDisposal,
            };
        },
        {
            atCost: 0,
            accumulated: 0,
            nbv: 0,
            sales: 0,
            gainOnDisposal: 0,
            lossOnDisposal: 0,
        }
    );

    const showTotals = storeRow.length > 1;

    return (
        <Box>
            <Table size="small">
                <TableHead>
                    <TableCell colSpan={9}>
                        <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                            {tableTitle}
                        </Typography>
                    </TableCell>
                </TableHead>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{fontWeight: 'bold'}} colSpan={3}></TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            At cost
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            Accumulated
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            NBV
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            Sales proceed
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            Gain on disposal
                        </TableCell>
                        <TableCell sx={{fontWeight: 'bold'}} align="center">
                            Loss on disposal
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {storeRow.map((row, index) => {
                        const nbv = row.atCost + row.accumulated;
                        const showGain = row.disposal >= 0;
                        const showLoss = row.disposal < 0;

                        return (
                            <TableRow key={index}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>
                                    <DatePicker
                                        value={row.date}
                                        onChange={() => {
                                        }}
                                        display={true}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                                        {row.item}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.atCost}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.accumulated}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={nbv}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <MoneyField
                                        value={row.sales}
                                        editable={false}
                                        currencySymbol={parentCurrency || ''}
                                        showCents={false}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {showGain ? (
                                        <MoneyField
                                            value={row.disposal}
                                            editable={false}
                                            currencySymbol={parentCurrency || ''}
                                            showCents={false}
                                            align="center"
                                        />
                                    ) : (
                                        <Typography variant="body2" align="center">
                                            -
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {showLoss ? (
                                        <MoneyField
                                            value={Math.abs(row.disposal)}
                                            editable={false}
                                            currencySymbol={parentCurrency || ''}
                                            showCents={false}
                                            align="center"
                                        />
                                    ) : (
                                        <Typography variant="body2" align="center">
                                            -
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {showTotals && (
                        <TableRow sx={{backgroundColor: '#f5f5f5'}}>
                            <TableCell colSpan={3} sx={{fontWeight: 'bold'}}>
                                Total
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.atCost}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.accumulated}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.nbv}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.sales}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.gainOnDisposal}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <MoneyField
                                    value={totals.lossOnDisposal}
                                    editable={false}
                                    currencySymbol={parentCurrency || ''}
                                    showCents={false}
                                    align="center"
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
}
